/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue, TimelinePosition } from './Timeline';

const TimelineManager: ComponentManagerFn<'Timeline'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'Timeline'>(platform, 'Timeline'),
    type: 'Timeline',
    name: 'Timeline',
    defaultChildren: ['TimelineItem'],
    isChildAllowed: (page, item, childType) =>
      (['TimelineItem'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      position: {
        label: 'Position',
        type: 'select',
        options: TimelinePosition,
      },
    },
    category: 'Data',
    icon: { iconName: 'clock', iconType: 'light' },
    description: `**Overview**<br/>
The Timeline component is a structural container used to display a sequence of events in chronological order. It provides a vertical visual "spine" that connects distinct moments, making it easy for users to understand the history or progress of a record.
Use this component to visualize Activity Logs (e.g., "User logged in," "File uploaded"), Order Status History (e.g., "Placed," "Shipped," "Delivered"), or Case Milestones.

**1. Layout & Alignment**<br/>
The Timeline manages the overall arrangement of the events relative to the central connecting line.
* **Position:** Controls how the content aligns.
    * **Left / Right:** Aligns all events to one side of the line. Best for simple lists or sidebars.
    * **Alternate:** Staggers events left and right along the center line. Best for detailed histories where you want to maximize space and visual interest.



**2. Content Composition**<br/>
The Timeline is a strict container. It defines the structure, but the actual content lives inside child components.
* **Allowed Children:** You can only add Timeline Item components directly to a Timeline.
* **Relationship:** The Timeline acts as the "Track," while each Timeline Item acts as a specific "Event Node" on that track.
* **Default State:** When added, it automatically includes one TimelineItem to get you started.

**3. Data Handling**<br/>
* **Visual Only:** The Timeline component itself does not bind to data or emit values. It simply organizes its children. To make it dynamic (e.g., showing a list of database records), you typically wrap the internal TimelineItem components in a repeater or bind their properties to a Data Store.`,
  };
};

export default TimelineManager;
